import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { ScheduleRequestEventData, useCalendarEventsConfigurationShallowStore } from '@frontend/schedule-helpers';
import { OperatoryAvailabilityBanner } from '@frontend/schedule-operatory-availability-banner';
import { InsuranceEligibilityCalendarCallout } from '@frontend/schedule-pulse-insurance-tab';
import { ContentLoader, useAlert } from '@frontend/design-system';
import { CalendarLocationSelectorModal } from '../../components/calendar-location-selector-modal';
import { ColumnEntityReorderActions } from '../../components/calendar-view/calendar-column-entity-list-section/column-entity-reorder-actions';
import { useColumnEntityReorderStore } from '../../components/calendar-view/providers';
import { useGetCalendarEntityScheduleV3Data } from '../../hooks/scheduler-v3/use-get-calendar-schedule-v3-data';
import { useGetCalendarV3QueriesData } from '../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useCalendarStore } from '../../stores/calendar.store';
import { useScheduleActionsContextValue } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/use-schedule-actions-context-value';
import { useScheduleRequestModalContextValue } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/use-schedule-request-modal-context-value';
import { ScheduleRequestFormManager } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestList/ScheduleRequestFormManager';
import { ScheduleRequestPanel } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestModal/ScheduleRequestWrappers/ScheduleRequestPanel';
import { CalendarEventType } from '../Calendar/types';
import { isUUID } from '../Calendar/utils';
import { CalendarV3WithGrid } from './CalendarV3WithGrid';
import { CalendarHeader } from './components/header';

export const CalendarV3Container = () => {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const { getLocationTimeZone } = useSchedulingLocationInfo();
  const { setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore('setConfiguredCalendarEvent');
  const { isInReorderMode } = useColumnEntityReorderStore(['isInReorderMode']);

  const {
    calendarEventsWithConvertedTimes,
    providersData,
    operatoryData,
    calendarLocationIds,
    calendarCounts,
    calendarTodayDate,
    isLoadingCalendarEvents,
    scheduledRequestsWithConvertedTimes,
    refetchScheduleRequestList,
  } = useGetCalendarV3QueriesData();
  const { calendarEntitySchedulesQuery } = useGetCalendarEntityScheduleV3Data(calendarLocationIds);

  const { calendarProviderIds, calendarOperatories } = useCalendarStore(['calendarProviderIds', 'calendarOperatories']);

  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM();

  const { isIntegratedOffice, isLoading: isLoadingIntegrations } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { isV3OperatorySupported, isLoading: isLoadingV3OperatorySupported } =
    SchedulerV3.Helpers.useCheckV3OperatorySupport();
  const { calendarEntityType, setCalendarEntityType } = useCalendarStore([
    'calendarEntityType',
    'setCalendarEntityType',
  ]);

  const { hasAiReceptionistAccess } = useAiScheduleAccess();
  const showOperatoryAvailabilityBanner =
    calendarEntityType === 'operatory' && isV3OperatorySupported && !hasAiReceptionistAccess;

  const {
    selectedScheduleRequest: selectedScheduleRequestEventData,
    scheduleRequestModalContextValue,
    convertedScheduleRequest: selectedScheduleRequest,
  } = useScheduleRequestModalContextValue<ScheduleRequestEventData>({ refetchScheduleRequestList });

  const scheduleActionsContextValue = useScheduleActionsContextValue();

  // Reset calendar entity type to 'provider' for offices which do not have operatory calendar view enabled
  useEffect(() => {
    if (isLoadingV3OperatorySupported) return;

    if (!isV3OperatorySupported && calendarEntityType === 'operatory') {
      setCalendarEntityType('provider');
    }
  }, [
    calendarEntityType,
    isIntegratedOffice,
    isLoadingIntegrations,
    isLoadingV3OperatorySupported,
    isV3OperatorySupported,
    setCalendarEntityType,
  ]);

  // Handle error while fetching provider schedules
  useEffect(() => {
    if (calendarEntitySchedulesQuery.isError) {
      alert.error(t('Error fetching schedules'));
    }
  }, [alert, calendarEntitySchedulesQuery.isError, t]);

  const sortedOperatories = useMemo(() => {
    const operatories = calendarOperatories.length
      ? operatoryData?.operatories?.filter(({ id }) => calendarOperatories.includes(id))
      : operatoryData?.operatories ?? [];
    return (
      operatories?.sort((a, b) =>
        ((a.displayName || a.externalName) ?? '').localeCompare((b.displayName || b.externalName) ?? '')
      ) ?? []
    );
  }, [calendarOperatories, operatoryData?.operatories]);

  const sortedProviders = useMemo(() => {
    const providers =
      (calendarProviderIds.length
        ? providersData?.providers.filter((p) => calendarProviderIds.includes(p.id))
        : providersData?.providers) ?? [];
    return providers
      .map((p) => ({ ...p, fullName: getFullName(p) }))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [calendarProviderIds, providersData?.providers]);

  // TODO: Review this logic to handle the configured calendar event
  const handleConfiguredCalendarEvent = useCallback(
    (configuredCalendarEvent: CalendarEventType) => {
      const isProviderEvent = configuredCalendarEvent.providerId && isUUID(configuredCalendarEvent.providerId);
      const isOfficeEvent = configuredCalendarEvent.locationId && !configuredCalendarEvent.providerId;
      if (isOfficeEvent || isProviderEvent) {
        setConfiguredCalendarEvent(configuredCalendarEvent);
      }
    },
    [setConfiguredCalendarEvent]
  );

  return (
    <>
      <section css={containerStyles} id='calendar-view-wrapper'>
        <InsuranceEligibilityCalendarCallout />
        {showOperatoryAvailabilityBanner && <OperatoryAvailabilityBanner />}
        {isInReorderMode ? (
          <ColumnEntityReorderActions />
        ) : (
          <CalendarHeader
            calendarEventsCount={calendarCounts.total}
            insuranceAlertCalendarEventsCount={calendarCounts.insurance}
            unconfirmedCalendarEventsCount={calendarCounts.unconfirmed}
            calendarTodayDate={calendarTodayDate}
          />
        )}
        <div css={{ position: 'relative', overflow: 'auto', flex: 1 }}>
          <ContentLoader
            show={isLoadingCalendarEvents}
            message={t('Loading appointments...')}
            css={{ justifyContent: 'flex-start', paddingTop: 'calc(50vh - 150px)' }}
          />
          <CalendarV3WithGrid
            calendarEventsWithConvertedTimes={calendarEventsWithConvertedTimes}
            scheduledRequestsWithConvertedTimes={scheduledRequestsWithConvertedTimes}
            sortedProviders={sortedProviders}
            sortedOperatories={sortedOperatories}
            calendarEntitySchedules={calendarEntitySchedulesQuery.data}
            hasWSM={hasWSM}
            isLoading={isLoadingCalendarEvents}
            handleConfiguredCalendarEvent={handleConfiguredCalendarEvent}
            primaryTimezone={getLocationTimeZone(calendarLocationIds[0] ?? '')}
          />
        </div>
      </section>

      <CalendarLocationSelectorModal />
      {selectedScheduleRequestEventData && (
        <ScheduleRequestFormManager
          wrapper={ScheduleRequestPanel}
          scheduleRequestModalContextValue={scheduleRequestModalContextValue}
          selectedScheduleRequest={selectedScheduleRequest ?? undefined}
          scheduleActionsContextValue={scheduleActionsContextValue}
        />
      )}
    </>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
  containerType: 'inline-size',
});
